"use client";

import { type FC, useEffect, useState, memo, useCallback, useMemo } from "react";
import { useDeploysStore } from "@stores/deploys.store";
import { useAssets } from "@stores/assets.store";
import type { IAsset } from "@api/IWalletController";
import { useShallow } from "zustand/react/shallow";
import { WalletController } from "@api/WalletController";

import DeployTable from "./components/DeployTable";
import ListingTable from "./components/ListingTable";
import type { NewToken } from "./types/index.types";
import { TabLayout } from "@components/ui/tab-layout";

// Utility function to batch asset loading
const batchLoadAssets = async (
    findAssets: (guids: { guid: string }[]) => Promise<IAsset[]>,
    tokens: NewToken[],
): Promise<Map<string, IAsset>> => {
    // Collect all asset GUIDs that need to be loaded
    const assetGuidsToLoad: string[] = [];

    tokens.forEach((token) => {
        if (token.asset1 && typeof token.asset1 === "string") {
            assetGuidsToLoad.push(token.asset1);
        }
        if (token.asset2 && typeof token.asset2 === "string") {
            assetGuidsToLoad.push(token.asset2);
        }
    });

    // If no assets to load, return empty map
    if (assetGuidsToLoad.length === 0) {
        return new Map();
    }

    // Remove duplicates
    const uniqueGuids = [...new Set(assetGuidsToLoad)];

    // Load all assets in a single batch request
    const assets = await findAssets(uniqueGuids.map((guid) => ({ guid })));

    // Create a map for quick lookup
    const assetsMap = new Map<string, IAsset>();

    assets.forEach((asset) => {
        assetsMap.set(asset.guid, asset);
    });

    return assetsMap;
};

// Apply loaded assets to tokens
const applyAssetsToTokens = (tokens: Map<string, NewToken>, assetsMap: Map<string, IAsset>): Map<string, NewToken> => {
    const updatedTokens = new Map(tokens);

    updatedTokens.forEach((token, guid) => {
        const updatedToken = { ...token };
        let hasChanges = false;

        if (updatedToken.asset1 && typeof updatedToken.asset1 === "string") {
            const asset = assetsMap.get(updatedToken.asset1);
            if (asset) {
                updatedToken.asset1 = asset;
                hasChanges = true;
            }
        }

        if (updatedToken.asset2 && typeof updatedToken.asset2 === "string") {
            const asset = assetsMap.get(updatedToken.asset2);
            if (asset) {
                updatedToken.asset2 = asset;
                hasChanges = true;
            }
        }

        if (hasChanges) {
            updatedTokens.set(guid, updatedToken);
        }
    });

    return updatedTokens;
};

const TokenManager: FC<{ token: NewToken; onUpdate: (updatedToken: NewToken) => void }> = memo(
    ({ token, onUpdate }) => {
        const { findAssets } = useAssets();

        useEffect(() => {
            const updateToken = async () => {
                if (token.asset1 === null || token.asset2 === null) {
                    try {
                        const resp = await WalletController.updateDeployToken(token.guid);
                        if (resp.status === "success") {
                            const updatedToken = {
                                ...resp.data,
                                dex: findDex(resp.data.dex),
                            } as NewToken;

                            // Collect both assets to load in a single batch
                            const assetsToLoad: { guid: string }[] = [];

                            if (updatedToken.asset1 && typeof updatedToken.asset1 === "string") {
                                assetsToLoad.push({ guid: updatedToken.asset1 });
                            }

                            if (updatedToken.asset2 && typeof updatedToken.asset2 === "string") {
                                assetsToLoad.push({ guid: updatedToken.asset2 });
                            }

                            // Only make the API call if we have assets to load
                            if (assetsToLoad.length > 0) {
                                const assets = await findAssets(assetsToLoad);
                                const assetsMap = new Map<string, IAsset>();

                                assets.forEach((asset) => {
                                    assetsMap.set(asset.guid, asset);
                                });

                                // Apply loaded assets to the token
                                if (updatedToken.asset1 && typeof updatedToken.asset1 === "string") {
                                    const asset = assetsMap.get(updatedToken.asset1);
                                    if (asset) {
                                        updatedToken.asset1 = asset;
                                    }
                                }

                                if (updatedToken.asset2 && typeof updatedToken.asset2 === "string") {
                                    const asset = assetsMap.get(updatedToken.asset2);
                                    if (asset) {
                                        updatedToken.asset2 = asset;
                                    }
                                }
                            }

                            onUpdate(updatedToken);
                        }
                    } catch (error) {
                        console.error(`Failed to update token ${token.guid}:`, error);
                    }
                }
            };

            const timeoutId = setTimeout(updateToken, 5000); // Reduced timeout for faster updates
            return () => clearTimeout(timeoutId);
        }, [token, findAssets, onUpdate]);

        return null;
    },
);

TokenManager.displayName = "TokenManager";

function findDex(dex: number) {
    switch (dex) {
        case 0:
            return "DeDust";
        case 1:
            return "Ston.FiV1";
        case 2:
            return "Ston.FiV2";
        case 3:
            return "BlumPump";
        case 4:
            return "GasPump";
        default:
            return "unknown";
    }
}

const Deploy: FC = () => {
    const { initWebSocket, history, updateToken } = useDeploysStore(
        useShallow((state) => ({
            history: state.history,
            initWebSocket: state.initWebSocket,
            updateToken: state.updateToken,
        })),
    );
    const { findAssets } = useAssets();
    const [tokenMap, setTokenMap] = useState<Map<string, NewToken>>(new Map());
    const [isLoadingAssets, setIsLoadingAssets] = useState(false);

    // Initialize WebSocket connection
    useEffect(() => {
        initWebSocket();
    }, [initWebSocket]);

    // Update token map when history changes
    useEffect(() => {
        if (history.length === 0) return;

        const newTokenMap = new Map<string, NewToken>();
        history.forEach((token) => {
            newTokenMap.set(token.guid, token);
        });

        setTokenMap(newTokenMap);

        // Trigger immediate asset loading when history changes
        loadAssetsForTokens(newTokenMap);
    }, [history]);

    // Function to load assets for tokens
    const loadAssetsForTokens = useCallback(
        async (tokens: Map<string, NewToken>) => {
            if (tokens.size === 0 || isLoadingAssets) return;

            setIsLoadingAssets(true);

            try {
                // Get all tokens that need asset loading
                const tokensArray = Array.from(tokens.values());
                const tokensNeedingAssets = tokensArray.filter(
                    (token) =>
                        (token.asset1 && typeof token.asset1 === "string") ||
                        (token.asset2 && typeof token.asset2 === "string"),
                );

                if (tokensNeedingAssets.length === 0) {
                    setIsLoadingAssets(false);
                    return;
                }

                // Batch load all assets
                const assetsMap = await batchLoadAssets(findAssets, tokensNeedingAssets);

                // Apply loaded assets to tokens
                if (assetsMap.size > 0) {
                    const updatedTokenMap = applyAssetsToTokens(tokens, assetsMap);
                    setTokenMap(updatedTokenMap);
                }
            } catch (error) {
                console.error("Failed to load assets:", error);
            } finally {
                setIsLoadingAssets(false);
            }
        },
        [findAssets, isLoadingAssets],
    );

    useEffect(() => {
        loadAssetsForTokens(tokenMap);
    }, [loadAssetsForTokens]);

    const handleTokenUpdate = useCallback(
        (updatedToken: NewToken) => {
            setTokenMap((prev) => {
                const newMap = new Map(prev);
                newMap.set(updatedToken.guid, updatedToken);
                return newMap;
            });

            updateToken(updatedToken);
        },
        [updateToken],
    );

    const tokenArray = useMemo(() => Array.from(tokenMap.values()), [tokenMap]);

    const deployData = useMemo(
        () =>
            tokenArray.filter((item) => item.dex.toLowerCase() === "gaspump" || item.dex.toLowerCase() === "blumpump"),
        [tokenArray],
    );

    const listingData = useMemo(
        () =>
            tokenArray.filter((item) => item.dex.toLowerCase() !== "gaspump" && item.dex.toLowerCase() !== "blumpump"),
        [tokenArray],
    );

    // Only render TokenManager components for tokens that actually need updating
    const tokensNeedingUpdate = useMemo(
        () => tokenArray.filter((token) => token.asset1 === null || token.asset2 === null),
        [tokenArray],
    );

    return (
        <div className="w-full blobs">
            {tokensNeedingUpdate.map((token) => (
                <TokenManager key={token.guid} token={token} onUpdate={handleTokenUpdate} />
            ))}
            <TabLayout
                tabs={[
                    { value: "deploy", label: "Deploy" },
                    { value: "listing", label: "Listing" },
                ]}
            >
                <DeployTable history={deployData} />
                <ListingTable history={listingData} />
            </TabLayout>
        </div>
    );
};

export default Deploy;
